/**
 * PSTUDIO-3624: pick the value the Expanded Cell View panel should display.
 *
 * While a grid cell is edited INLINE, its keystrokes live in the store's
 * non-observable draft buffer (`draftCellValue`, kept non-reactive so the table
 * — a top-level MobX `observer` — does not re-render on every keystroke,
 * PSTUDIO-2821). The committed row value only updates on blur, so the panel,
 * which reads the committed `selectedCellValue()`, would show a stale value
 * mid-edit (Leo's PSTUDIO-3624 report: the side panel "doesn't show the live
 * updates" while you type in the cell).
 *
 * `liveCellDraft` is a narrow observable mirror of the draft that ONLY the panel
 * reads — so a keystroke re-renders the panel but NOT the table. This helper
 * decides between it and the committed value: show the live draft ONLY while
 * THIS selected cell is the active inline-edit target; anything else (no edit, a
 * different cell, or editing in the panel itself — which writes straight to the
 * observable row) falls back to the committed value.
 *
 * Note: an empty-string draft (`''`, user cleared the cell) is a real live value
 * and must win over the committed value — so the gate is `liveDraft != null`,
 * never a truthiness check.
 */
export function resolvePanelCellValue<T>(args: {
  selectedCell: { rowId: string; columnId: string } | undefined;
  editingRowId: string | null;
  editingColumnId: string | null;
  liveDraft: string | null;
  committedValue: T;
}): T | string {
  const {
    selectedCell,
    editingRowId,
    editingColumnId,
    liveDraft,
    committedValue,
  } = args;

  if (
    selectedCell &&
    liveDraft != null &&
    editingRowId === selectedCell.rowId &&
    editingColumnId === selectedCell.columnId
  ) {
    return liveDraft;
  }

  return committedValue;
}
